import { Button, TableItem, TableMessage } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useNewWindowNavigate } from 'hooks/useNewWindowNavigate';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useStoreState } from 'providers/StoreStateContext';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCalendar,
  PiCheck,
  PiClockCounterClockwise,
  PiNavigationArrow,
  PiPlus,
  PiTrash,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetContractsByLabel } from 'services/contract';
import { PRODUCT_TYPE } from 'services/contract/types';
import { useGetLabelLightList } from 'services/labels/light';
import { DIGITAL_PRODUCT_TYPE } from 'services/music-product-digital/types/enums';
import {
  IMusicProductDigitalForm,
  IMusicProductDigitalSettlementInfoPerTrackContractForm,
  ISettlementInfoContractForm,
} from 'services/music-product-digital/types/form';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDate, formatDateToInput } from 'utils/global/date.utils';

interface IMusicProductDigitalContractSeletcProps
  extends ISettlementInfoContractForm {
  id: number;
  trackId?: number;
}

const MusicProductDigitalContractSeletc = ({
  id,
  contractId,
  contractName,
  labelId,
  trackId,
}: IMusicProductDigitalContractSeletcProps) => {
  const {
    values,
    setFieldValue,
  }: IFormikContextType<IMusicProductDigitalForm> =
    useFormikContext<IMusicProductDigitalForm>();
  const { t } = useTranslation();

  const labelContracts = useGetContractsByLabel(labelId, {
    params: {
      itemCount: 999,
      page: 0,
      ProductType: PRODUCT_TYPE.Digital,
      ShowOnlyActive: true,
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const contractOptions =
    labelContracts.data?.pages
      .flat()
      .filter(labelContract =>
        trackId !== undefined
          ? !values.settlementInfo.perTrackContracts[trackId].contracts
              .map(c => c.contractId)
              .includes(labelContract.id)
          : !values.settlementInfo.contracts
              .map(c => c.contractId)
              .includes(labelContract.id)
      )
      .map(contract => ({ value: contract.id, label: contract.title })) ?? [];

  useEffect(() => {
    labelContracts.refetch();
  }, [labelId]);

  return (
    <FormInput.Select
      name={
        trackId !== undefined
          ? `settlementInfo.perTrackContracts[${trackId}].contracts[${id}].contractId`
          : `settlementInfo.contracts[${id}].contractId`
      }
      isAsync
      isSearchable
      label={t(`input.label.contract`)}
      onChange={value => {
        setFieldValue(
          trackId !== undefined
            ? `settlementInfo.perTrackContracts[${trackId}].contracts[${id}].contractName`
            : `settlementInfo.contracts[${id}].contractName`,
          value.label
        );
      }}
      value={{
        label: contractName,
        value: contractId,
      }}
      options={contractOptions}
      isLoading={labelContracts.isLoading}
    />
  );
};

const MusicProductDigitalContract = () => {
  const {
    values,
    status,
    setFieldValue,
    initialValues,
  }: IFormikContextType<IMusicProductDigitalForm> =
    useFormikContext<IMusicProductDigitalForm>();
  usePermissionGuard('ContractPermissionsLIST**');
  const { t } = useTranslation();
  const navigateToNewWindow = useNewWindowNavigate();
  const navigate = useNavigate();

  const labels = useGetLabelLightList();

  const { applicationSettings } = useStoreState();

  const getLabelOptions = (contracts: ISettlementInfoContractForm[]) =>
    labels.data
      ?.filter(label =>
        applicationSettings?.allowContractsWithSameLabel
          ? true
          : !contracts.map(contract => contract.labelId).includes(label.id)
      )
      .map(label => ({ value: label.id, label: label.labelName })) ?? [];

  const nodes = values.settlementInfo.contracts.map((contract, index) => ({
    ...contract,
    id: index,
  }));

  const getPerTrackNodes = (trackIndex: number) =>
    values.settlementInfo.perTrackContracts.length
      ? values.settlementInfo.perTrackContracts[trackIndex].contracts.map(
          (contract, index) => ({
            ...contract,
            id: index,
          })
        )
      : [];

  const getDivisionPercentageSum = (contracts: ISettlementInfoContractForm[]) =>
    contracts
      .map(contract => contract.divisionPercentage ?? 0)
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0) ??
    0;

  const getContractsWithoutDivision = (
    contracts: ISettlementInfoContractForm[]
  ) =>
    contracts.filter(
      ({ divisionPercentage }) =>
        divisionPercentage === null || divisionPercentage === undefined
    ).length ?? 1;

  const getPercentageLeft = (contracts: ISettlementInfoContractForm[]) =>
    getDivisionPercentageSum(contracts) < 100
      ? parseFloat(
          (
            (100 - getDivisionPercentageSum(contracts)) /
            getContractsWithoutDivision(contracts)
          ).toFixed(2)
        )
      : 0;

  const onAddContract = () => {
    setFieldValue(
      `settlementInfo.contracts[${values.settlementInfo.contracts.length}]`,
      {
        contractId: '',
        contractName: '',
        labelId: '',
        labelName: '',
        startDate: formatDateToInput(new Date()),
        endDate: null,
        divisionPercentage: null,
      }
    );
  };

  const onRemoveContract = (index: number) => {
    setFieldValue(
      'settlementInfo.contracts',
      values.settlementInfo.contracts.filter((_, key) => key !== index)
    );
  };

  const onAddContractPerTrack = (trackIndex: number) => {
    setFieldValue(
      `settlementInfo.perTrackContracts[${trackIndex}].contracts[${values.settlementInfo.perTrackContracts[trackIndex].contracts.length}]`,
      {
        contractId: '',
        contractName: '',
        labelId: '',
        labelName: '',
        startDate: formatDateToInput(new Date()),
        endDate: null,
        divisionPercentage: null,
      }
    );
  };

  const onRemoveContractPerTrack = (trackIndex: number, index: number) => {
    setFieldValue(
      `settlementInfo.perTrackContracts[${trackIndex}].contracts`,
      values.settlementInfo.perTrackContracts[trackIndex].contracts.filter(
        (_, key) => key !== index
      )
    );
  };

  const getAllContractsSelected = (contracts: ISettlementInfoContractForm[]) =>
    !!contracts.length
      ? !!contracts[contracts.length - 1].contractId.length
      : true;

  useEffect(() => {
    if (values.settlementInfo.perTrackSettlement) {
      const tracks = values.tracks
        .map(album => album.tracks)
        .flat()
        .filter(track => track.id.length)
        .map(tracks => tracks.id);


      const perSettlementTracks = values.settlementInfo.perTrackContracts.map(
        track => track.trackId
      );


      const overdueTracks = values.settlementInfo.perTrackContracts
        .filter(track => !tracks.includes(track.trackId))
        .map(track => track.trackId);


      const missingTracks = values.tracks
        .map(album => album.tracks)
        .flat()
        .filter(track => track.id.length)
        .filter(track => !perSettlementTracks.includes(track.id));


      const contracts: IMusicProductDigitalSettlementInfoPerTrackContractForm[] =
        values.settlementInfo.perTrackContracts.filter(
          track => !overdueTracks.includes(track.trackId)
        );

      const perContractTracksLength =
        values.settlementInfo.perTrackContracts.length;


      for (const missingTrack of missingTracks) {
        contracts.unshift({
          contracts: [],
          trackId: missingTrack.id,
          title: missingTrack.name,
        });
      }

      setFieldValue('settlementInfo.perTrackContracts', contracts);
    }
  }, [
    undefined,
    initialValues.settlementInfo,
    values.settlementInfo.perTrackSettlement,
  ]);

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          hideHeader
          topbarContent={
            <div className='flex w-full justify-between'>
              {values.productType === DIGITAL_PRODUCT_TYPE.audio ? (
                <div className='flex gap-4'>
                  <div className='w-60'>
                    <FormInput.Select
                      name='settlementInfo.perTrackSettlement'
                      label={t('input.label.per-track-settlement')}
                      options={[
                        { label: t('input.option.yes'), value: true },
                        { label: t('input.option.no'), value: false },
                      ]}
                    />
                  </div>
                  {!values.settlementInfo.perTrackSettlement && (
                    <div className='w-60'>
                      <FormInput.Select
                        name='settlementInfo.showEveryTrackInReport'
                        label={t('input.label.show-tracks-on-report')}
                        options={[
                          { label: t('input.option.yes'), value: true },
                          { label: t('input.option.no'), value: false },
                        ]}
                        isClearable={false}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div></div>
              )}
              <div className='flex gap-2'>
                <Button
                  onlyIcon
                  isClear
                  onClick={() => navigate('history')}
                  disabled={!initialValues.mainTitle.title.length}>
                                    <Button.Description description={t('history.contract')} />

                                    <Button.Icon icon={<PiClockCounterClockwise />} />
                </Button>
                <Button
                  onlyIcon
                  isClear
                  onClick={() => navigate('future')}
                  disabled={!initialValues.mainTitle.title.length}>
                                    <Button.Description description={t('history.contract-future')} />

                                    <Button.Icon icon={<PiCalendar />} />
                </Button>
              </div>
            </div>
          }
        />
      }>
      <div className='flex flex-col-reverse gap-8 w-full'>
        <div className='flex flex-col-reverse mb-20 w-full gap-2'>
          {values.settlementInfo.perTrackSettlement &&
            (!values.settlementInfo.perTrackContracts.length
              ? 'Add at least one track to the product'
              : values.settlementInfo.perTrackContracts.map(
                  (track, trackIndex) => (
                    <Table
                      id={'MusicProductDigitalContract'}
                      key={trackIndex}
                      size='compact'
                      data={{
                        nodes: getPerTrackNodes(trackIndex),
                      }}
                      columns={[
                        {
                          label: `${track.title}`,
                          renderCell: ({
                            labelName: name,
                            labelId: id,
                            id: index,
                          }) => (
                            <TableItem>
                              {status?.displayMode ? (
                                <TableItem.Text
                                  text={name}
                                  className={id ? 'cursor-pointer' : undefined}
                                  onClick={
                                    id
                                      ? () =>
                                          navigateToNewWindow(`label/${id}/finances`)
                                      : undefined
                                  }
                                />
                              ) : (
                                <FormInput.Select
                                  name={`settlementInfo.perTrackContracts[${trackIndex}].contracts[${index}].labelId`}
                                  isAsync
                                  isSearchable
                                  label={t(`input.label.label`)}
                                  onChange={value => {
                                    setFieldValue(
                                      `settlementInfo.perTrackContracts[${trackIndex}].contracts[${index}].labelName`,
                                      value.label
                                    );
                                    setFieldValue(
                                      `settlementInfo.perTrackContracts[${trackIndex}].contracts[${index}].contractName`,
                                      ' '
                                    );
                                    setFieldValue(
                                      `settlementInfo.perTrackContracts[${trackIndex}].contracts[${index}].contractId`,
                                      ' '
                                    );
                                  }}
                                  value={{
                                    label: name,
                                    value: id,
                                  }}
                                  options={getLabelOptions(track.contracts)}
                                  isLoading={labels.isLoading}
                                />
                              )}
                            </TableItem>
                          ),
                          resize: true,
                        },
                        {
                          label: t(`input.label.contract`),
                          renderCell: contract => (
                            <TableItem>
                              {status?.displayMode ? (
                                <TableItem.Text text={contract.contractName} />
                              ) : !contract.labelId.length ? (
                                <FormInput.Select
                                  name={`settlementInfo.perTrackContracts[${trackIndex}].contracts[${contract.id}].contractId`}
                                  label={t(`input.label.contract`)}
                                  isDisabled
                                />
                              ) : (
                                <MusicProductDigitalContractSeletc
                                  {...contract}
                                  trackId={trackIndex}
                                />
                              )}
                            </TableItem>
                          ),
                          resize: true,
                        },
                        {
                          label: t(`input.label.date`),
                          renderCell: contract => (
                            <TableItem>
                              {status?.displayMode ? (
                                <TableItem.Text
                                  text={`${formatDate(contract.startDate)}`}
                                />
                              ) : (
                                <FormInput.Text
                                  name={`settlementInfo.perTrackContracts[${trackIndex}].contracts[${contract.id}].startDate`}
                                  type='date'
                                  label={t('input.label.startDate')}
                                />
                              )}
                            </TableItem>
                          ),
                          resize: true,
                        },
                        {
                          label: t(`input.label.division`),
                          renderCell: ({ divisionPercentage, id: index }) => (
                            <TableItem>
                              {status?.displayMode ? (
                                <TableItem.Text
                                  text={`${divisionPercentage ?? '-'}%`}
                                />
                              ) : (
                                <div className='flex w-full gap-2'>
                                  <div>
                                    <FormInput.Number
                                      name={`settlementInfo.perTrackContracts[${trackIndex}].contracts[${index}].divisionPercentage`}
                                      label={t(`input.label.division`)}
                                      suffix=' %'
                                      placeholder={`${getPercentageLeft(
                                        track.contracts
                                      )} %`}
                                    />
                                  </div>
                                  {values.settlementInfo.perTrackContracts[
                                    trackIndex
                                  ].contracts[index].divisionPercentage ===
                                    null && (
                                    <Button
                                      size='compact'
                                      isClear
                                      onlyIcon
                                      onClick={() =>
                                        setFieldValue(
                                          `settlementInfo.perTrackContracts[${trackIndex}].contracts[${index}].divisionPercentage`,
                                          getPercentageLeft(track.contracts)
                                        )
                                      }>
                                                                            <Button.Description description={t('button.accept')} />

                                                                            <Button.Icon icon={<PiCheck />} />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </TableItem>
                          ),
                        },

                        {
                          label: '',
                          renderCell: ({ id: index, labelId, contractId }) => (
                            <TableItem justifyPosition='end'>
                              {status?.displayMode ? (
                                <Button
                                  isClear
                                  onlyIcon
                                  onClick={() =>
                                    navigateToNewWindow(
                                      `label/${labelId}/contract/${contractId}/form`
                                    )
                                  }>
                                  <Button.Description
                                    description={t('button.go-to-form')}
                                  />
                                  <Button.Icon
                                    icon={
                                      <PiNavigationArrow transform='scale(-1,1)' />
                                    }
                                  />
                                </Button>
                              ) : (
                                <Button
                                  variant={BUTTON_VARIANT.remove}
                                  isClear
                                  onlyIcon
                                  onClick={() =>
                                    onRemoveContractPerTrack(trackIndex, index)
                                  }>
                                                                    <Button.Description description={t('button.remove')} />

                                                                    <Button.Icon icon={<PiTrash />} />
                                </Button>
                              )}
                            </TableItem>
                          ),
                        },
                      ]}
                      tableOptions={
                        status?.displayMode
                          ? undefined
                          : {
                              renderAfterTable: () => (
                                <tfoot className='full-width-footer'>
                                  <TableMessage
                                    className='cursor-pointer'
                                    disable={
                                      !getAllContractsSelected(track.contracts)
                                    }
                                    onClick={() =>
                                      onAddContractPerTrack(trackIndex)
                                    }>
                                    <TableItem.Text text={t('button.add')} />
                                    <PiPlus />
                                  </TableMessage>
                                </tfoot>
                              ),
                            }
                      }
                    />
                  )
                ))}
        </div>
        <div className='flex w-full'>
          <Table
            id={'MusicProductDigitalContract'}
            size='compact'
            data={{ nodes: nodes }}
            columns={[
              {
                label: `${t('input.label.global')}`,
                renderCell: ({ labelName: name, labelId: id, id: index }) => (
                  <TableItem>
                    {status?.displayMode ? (
                      <TableItem.Text
                        text={name}
                        className={id ? 'cursor-pointer' : undefined}
                        onClick={
                          id
                            ? () => navigateToNewWindow(`label/${id}/finances`)
                            : undefined
                        }
                      />
                    ) : (
                      <FormInput.Select
                        name={`settlementInfo.contracts[${index}].labelId`}
                        isAsync
                        isSearchable
                        label={t(`input.label.label`)}
                        onChange={value => {
                          setFieldValue(
                            `settlementInfo.contracts[${index}].labelName`,
                            value.label
                          );
                          setFieldValue(
                            `settlementInfo.contracts[${index}].contractName`,
                            ' '
                          );
                          setFieldValue(
                            `settlementInfo.contracts[${index}].contractId`,
                            ' '
                          );
                        }}
                        value={{
                          label: name,
                          value: id,
                        }}
                        options={getLabelOptions(
                          values.settlementInfo.contracts
                        )}
                        isLoading={labels.isLoading}
                      />
                    )}
                  </TableItem>
                ),
                resize: true,
              },
              {
                label: t(`input.label.contract`),
                renderCell: contract => (
                  <TableItem>
                    {status?.displayMode ? (
                      <TableItem.Text text={contract.contractName} />
                    ) : !contract.labelId.length ? (
                      <FormInput.Select
                        name={`settlementInfo.contracts[${contract.id}].contractId`}
                        label={t(`input.label.contract`)}
                        isDisabled
                      />
                    ) : (
                      <MusicProductDigitalContractSeletc {...contract} />
                    )}
                  </TableItem>
                ),
                resize: true,
              },
              {
                label: t(`input.label.date`),
                renderCell: contract => (
                  <TableItem>
                    {status?.displayMode ? (
                      <TableItem.Text
                        text={`${formatDate(contract.startDate)}`}
                      />
                    ) : (
                      <FormInput.Text
                        name={`settlementInfo.contracts[${contract.id}].startDate`}
                        type='date'
                        label={t('input.label.startDate')}
                      />
                    )}
                  </TableItem>
                ),
                resize: true,
              },

              {
                label: t(`input.label.division`),
                renderCell: ({ divisionPercentage, id: index }) => (
                  <TableItem>
                    {status?.displayMode ? (
                      <TableItem.Text text={`${divisionPercentage ?? '-'}%`} />
                    ) : (
                      <div className='flex w-full gap-2'>
                        <div>
                          <FormInput.Number
                            name={`settlementInfo.contracts[${index}].divisionPercentage`}
                            label={t(`input.label.division`)}
                            suffix=' %'
                            placeholder={`${getPercentageLeft(
                              values.settlementInfo.contracts
                            )} %`}
                          />
                        </div>
                        {values.settlementInfo.contracts[index]
                          .divisionPercentage === null && (
                          <Button
                            size='compact'
                            isClear
                            onlyIcon
                            onClick={() =>
                              setFieldValue(
                                `settlementInfo.contracts[${index}].divisionPercentage`,
                                getPercentageLeft(
                                  values.settlementInfo.contracts
                                )
                              )
                            }>
                                                        <Button.Description description={t('button.accept')} />

                                                        <Button.Icon icon={<PiCheck />} />
                          </Button>
                        )}
                      </div>
                    )}
                  </TableItem>
                ),
              },
              {
                label: '',
                renderCell: ({ id: index, labelId, contractId }) => (
                  <TableItem justifyPosition='end'>
                    {status?.displayMode ? (
                      <Button
                        isClear
                        onlyIcon
                        onClick={() =>
                          navigateToNewWindow(
                            `label/${labelId}/contract/${contractId}/form`
                          )
                        }>
                                                <Button.Description description={t('button.go-to-form')} />

                                                <Button.Icon
                          icon={<PiNavigationArrow transform='scale(-1,1)' />}
                        />
                      </Button>
                    ) : (
                      <Button
                        variant={BUTTON_VARIANT.remove}
                        isClear
                        onlyIcon
                        onClick={() => onRemoveContract(index)}>
                                                <Button.Description description={t('button.remove')} />

                                                <Button.Icon icon={<PiTrash />} />
                      </Button>
                    )}
                  </TableItem>
                ),
              },
            ]}
            tableOptions={
              status?.displayMode
                ? undefined
                : {
                    renderAfterTable: () => (
                      <tfoot className='full-width-footer'>
                        <TableMessage
                          className='cursor-pointer'
                          disable={
                            !getAllContractsSelected(
                              values.settlementInfo.contracts
                            )
                          }
                          onClick={onAddContract}>
                          <TableItem.Text text={t('button.add')} />
                          <PiPlus />
                        </TableMessage>
                      </tfoot>
                    ),
                  }
            }
          />
        </div>
      </div>
    </TopbarPage>
  );
};

export default MusicProductDigitalContract;
